import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { h2hAPI } from '../api/endpoints';
import { formatXPUNT, formatTimeAgo } from '../utils/helpers';
import { FaPlus, FaSearch, FaHandshake, FaUsers, FaClock } from 'react-icons/fa';
import LoadingSpinner from '../components/common/LoadingSpinner';
import './H2H.css';

const H2H = () => {
  const { isAuthenticated, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [games, setGames] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, pages: 0, current: 1 });
  const [filters, setFilters] = useState({ status: 'open', search: '' });

  const fetchGames = async () => {
    setLoading(true);
    try {
      const params = {
        per_page: 20,
        page: pagination.current,
        status: filters.status || undefined,
      };
      const response = await h2hAPI.getList(params);
      setGames(response.data.data || []);
      setPagination({
        total: response.data.total || 0,
        pages: response.data.pages || 0,
        current: pagination.current,
      });
    } catch (error) {
      console.error('Failed to fetch H2H games:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGames();
  }, [pagination.current, filters.status]);

  const handleFilterChange = (e) => {
    setFilters(prev => ({ ...prev, status: e.target.value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  if (loading) {
    return (
      <div className="h2h-loading">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="h2h-page">
      <div className="page-header">
        <div>
          <h1><FaHandshake className="header-icon" /> H2H Games</h1>
          <p className="page-subtitle">Challenge other punters head-to-head</p>
        </div>
        {isAuthenticated && user?.punter_plus && (
          <Link to="/h2h/create" className="btn btn-primary">
            <FaPlus /> Create H2H
          </Link>
        )}
      </div>

      {/* Filters */}
      <div className="h2h-filters">
        <div className="filter-group">
          <label>Status</label>
          <select value={filters.status} onChange={handleFilterChange}>
            <option value="open">Open</option>
            <option value="completed">Completed</option>
            <option value="refunded">Refunded</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Search</label>
          <div className="search-input-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search games..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="search-input"
            />
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="results-info">
        <span>{pagination.total} games found</span>
      </div>

      {/* Games Grid */}
      {games.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🤝</div>
          <h3>No H2H games found</h3>
          <p>Be the first to create a head-to-head challenge</p>
        </div>
      ) : (
        <div className="h2h-grid">
          {games.map((game) => (
            <Link to={`/h2h/${game.id}`} key={game.id} className="h2h-card">
              <div className="h2h-card-header">
                <span className={`h2h-status ${game.status}`}>
                  {game.status}
                </span>
                <span className="h2h-pot">{formatXPUNT(game.total_pot)}</span>
              </div>
              <div className="h2h-teams">
                <span className="team">{game.home_team}</span>
                <span className="vs">vs</span>
                <span className="team">{game.away_team}</span>
              </div>
              <div className="h2h-details">
                <div className="detail">
                  <FaUsers className="detail-icon" />
                  <span>{game.players_count || 0} players</span>
                </div>
                <div className="detail">
                  <FaClock className="detail-icon" />
                  <span>{formatTimeAgo(game.created_at)}</span>
                </div>
                <div className="detail">
                  <span className="min-stake">Min: {formatXPUNT(game.minimum_stake)}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => setPagination(prev => ({ ...prev, current: prev.current - 1 }))}
            disabled={pagination.current === 1}
            className="pagination-btn"
          >
            Previous
          </button>
          <div className="pagination-pages">
            {Array.from({ length: Math.min(pagination.pages, 5) }, (_, i) => {
              let pageNum;
              if (pagination.pages <= 5) {
                pageNum = i + 1;
              } else if (pagination.current <= 3) {
                pageNum = i + 1;
              } else if (pagination.current >= pagination.pages - 2) {
                pageNum = pagination.pages - 4 + i;
              } else {
                pageNum = pagination.current - 2 + i;
              }
              return (
                <button
                  key={pageNum}
                  onClick={() => setPagination(prev => ({ ...prev, current: pageNum }))}
                  className={`pagination-page ${pagination.current === pageNum ? 'active' : ''}`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          <button 
            onClick={() => setPagination(prev => ({ ...prev, current: prev.current + 1 }))}
            disabled={pagination.current === pagination.pages}
            className="pagination-btn"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default H2H;